/**
 * Iter 2026-02 · Cross-role project assignment card.
 *
 * Renders three columns (Technical · Commercial · Support), each with:
 *   - Current assignee chips (name + remove-x for editors)
 *   - "Assign" button that opens a multi-select picker of active users
 *
 * Editors are users with `legacy_project:assign` permission OR anyone already
 * assigned to the project (self-editable team model, per operator
 * directive 2026-02-16).
 */
import { useMemo, useState } from "react";
import { UserCircle, Plus, X, CheckSquare, Square, Wrench, HandCoins, Headset } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";

const ROLE_META = {
    technical: {
        label: "Technical",
        icon: Wrench,
        hint: "Developers · delivery",
        colour: "text-indigo-700 bg-indigo-50 border-indigo-200",
        headerColour: "text-indigo-700",
    },
    commercial: {
        label: "Commercial",
        icon: HandCoins,
        hint: "Accounts liaison",
        colour: "text-emerald-700 bg-emerald-50 border-emerald-200",
        headerColour: "text-emerald-700",
    },
    support: {
        label: "Support",
        icon: Headset,
        hint: "Post-delivery support",
        colour: "text-amber-700 bg-amber-50 border-amber-200",
        headerColour: "text-amber-700",
    },
};

const ROLES = ["technical", "commercial", "support"];

export default function ProjectAssignmentsCard({ project, canAssign, onChange }) {
    const assignments = project.assignments || { technical: [], commercial: [], support: [] };

    return (
        <div className="reedos-card p-5" data-testid="project-assignments-card">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="font-display text-sm font-semibold text-slate-900 flex items-center gap-2">
                        <UserCircle size={16} weight="duotone" className="text-slate-500" />
                        Team assignments
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">Assign this project to Technical, Commercial and Support owners. Each owner sees it in <span className="font-medium">My Projects</span>.</p>
                </div>
                {!canAssign && (
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Read-only</span>
                )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {ROLES.map((role) => (
                    <RoleColumn
                        key={role}
                        role={role}
                        entries={assignments[role] || []}
                        projectId={project.id}
                        canAssign={canAssign}
                        onChange={onChange}
                    />
                ))}
            </div>
        </div>
    );
}

function RoleColumn({ role, entries, projectId, canAssign, onChange }) {
    const [pickerOpen, setPickerOpen] = useState(false);
    const meta = ROLE_META[role];
    const Icon = meta.icon;

    const removeUser = async (userId) => {
        try {
            const { data } = await api.post(`/billing/project/${projectId}/assign`, {
                role,
                user_ids: [userId],
                mode: "remove",
            });
            onChange?.(data.assignments);
            toast.success(`Removed from ${meta.label}`);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to remove assignee"));
        }
    };

    return (
        <div
            className="rounded-lg border border-slate-200 bg-slate-50/40 p-3 flex flex-col gap-2"
            data-testid={`project-assign-col-${role}`}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                    <Icon size={14} weight="duotone" className={meta.headerColour} />
                    <span className={`text-xs font-semibold uppercase tracking-wider ${meta.headerColour}`}>{meta.label}</span>
                    <span className="text-[10px] text-slate-400">· {meta.hint}</span>
                </div>
                {canAssign && (
                    <button
                        type="button"
                        onClick={() => setPickerOpen(true)}
                        className="text-[11px] font-semibold text-slate-700 hover:text-slate-900 inline-flex items-center gap-0.5"
                        data-testid={`project-assign-add-${role}`}
                    >
                        <Plus size={12} weight="bold" /> Assign
                    </button>
                )}
            </div>
            {entries.length === 0 ? (
                <div className="text-[11px] text-slate-400 italic py-1.5">No {meta.label.toLowerCase()} owner assigned.</div>
            ) : (
                <div className="flex flex-wrap gap-1.5">
                    {entries.map((e) => (
                        <span
                            key={e.user_id}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${meta.colour}`}
                            title={`${e.user_email}${e.assigned_by_name ? ` · assigned by ${e.assigned_by_name}` : ""}`}
                            data-testid={`project-assign-chip-${role}-${e.user_id}`}
                        >
                            {e.user_name}
                            {canAssign && (
                                <button
                                    type="button"
                                    onClick={() => removeUser(e.user_id)}
                                    className="ml-0.5 opacity-70 hover:opacity-100"
                                    aria-label={`Remove ${e.user_name}`}
                                    data-testid={`project-assign-remove-${role}-${e.user_id}`}
                                >
                                    <X size={10} weight="bold" />
                                </button>
                            )}
                        </span>
                    ))}
                </div>
            )}
            {pickerOpen && (
                <AssignPickerDialog
                    role={role}
                    roleLabel={meta.label}
                    projectId={projectId}
                    existing={entries}
                    onClose={() => setPickerOpen(false)}
                    onCommit={(assignments) => {
                        onChange?.(assignments);
                        setPickerOpen(false);
                    }}
                />
            )}
        </div>
    );
}

function AssignPickerDialog({ role, roleLabel, projectId, existing, onClose, onCommit }) {
    const [q, setQ] = useState("");
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [selected, setSelected] = useState(() => new Set(existing.map((e) => e.user_id)));

    const search = async (query) => {
        setLoading(true);
        try {
            const { data } = await api.get(`/billing/project/_meta/pickable-users`, { params: { q: query || undefined } });
            setUsers(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load users"));
        } finally {
            setLoading(false);
        }
    };

    // Initial load
    useMemo(() => { search(""); /* eslint-disable-next-line */ }, []);

    const toggle = (uid) => {
        const s = new Set(selected);
        if (s.has(uid)) s.delete(uid);
        else s.add(uid);
        setSelected(s);
    };

    const commit = async () => {
        setSubmitting(true);
        try {
            const { data } = await api.post(`/billing/project/${projectId}/assign`, {
                role,
                user_ids: Array.from(selected),
                mode: "replace",
            });
            const added = Array.from(selected).filter((uid) => !existing.find((e) => e.user_id === uid));
            const removed = existing.filter((e) => !selected.has(e.user_id));
            const msgs = [];
            if (added.length) msgs.push(`${added.length} added`);
            if (removed.length) msgs.push(`${removed.length} removed`);
            toast.success(msgs.length ? `${roleLabel} owners updated · ${msgs.join(" · ")}` : `${roleLabel} owners saved`);
            onCommit(data.assignments);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to save assignments"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-md" data-testid={`project-assign-picker-${role}`}>
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        Assign {roleLabel} owners
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        Select any active user. Newly-added owners receive an email notification and see this project on their <span className="font-medium">My Projects</span> page.
                    </DialogDescription>
                </DialogHeader>
                <Input
                    autoFocus
                    placeholder="Search by name or email…"
                    value={q}
                    onChange={(e) => {
                        setQ(e.target.value);
                        // Debounce would be nice but 50-limit endpoint + local render is fast.
                        search(e.target.value);
                    }}
                    data-testid={`project-assign-search-${role}`}
                />
                <div className="max-h-72 overflow-y-auto space-y-1">
                    {loading && <div className="text-xs text-slate-500 p-2">Loading…</div>}
                    {!loading && users.length === 0 && (
                        <div className="text-xs text-slate-500 p-2">No users match your search.</div>
                    )}
                    {users.map((u) => {
                        const on = selected.has(u.id);
                        return (
                            <button
                                key={u.id}
                                type="button"
                                onClick={() => toggle(u.id)}
                                className={`w-full text-left px-2.5 py-2 rounded-md flex items-center gap-2 border transition-colors ${
                                    on ? "border-indigo-300 bg-indigo-50" : "border-transparent hover:bg-slate-50"
                                }`}
                                data-testid={`project-assign-user-${u.id}`}
                            >
                                {on ? <CheckSquare size={16} className="text-indigo-600" weight="fill" /> : <Square size={16} className="text-slate-400" />}
                                <div className="flex flex-col leading-tight text-left min-w-0">
                                    <span className="text-sm text-slate-900 truncate">{u.name || u.email}</span>
                                    <span className="text-[10px] text-slate-500 truncate">{u.email}{u.role_names?.length ? ` · ${u.role_names.join(", ")}` : ""}</span>
                                </div>
                            </button>
                        );
                    })}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
                    <Button
                        onClick={commit}
                        disabled={submitting}
                        className="bg-slate-900 hover:bg-slate-800 text-white"
                        data-testid={`project-assign-save-${role}`}
                    >
                        {submitting ? "Saving…" : `Save ${selected.size} owner${selected.size === 1 ? "" : "s"}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
